// Export presets (§11.13). Built-ins are editable and duplicable: editing a built-in stores a user copy
// with the same id in IndexedDB, which then wins. Delivery-app sizes are editable defaults, labelled
// "check the app's current spec" (D7) — we do not claim official sizes.
import { createStore, get, set, type UseStore } from 'idb-keyval';
import type { ExportPreset } from '../model/types';

// GPS (owner decision, CODE_REVIEW B3): stripped by default; kept only on the archive presets.
// The export dialog's metadata choice still overrides it.
const KEEP_GPS = new Set(['full-res-jpeg', 'print-300', 'png16-master', 'tiff16']);

const base = (
  p: Partial<ExportPreset> & Pick<ExportPreset, 'id' | 'name' | 'nameEs' | 'format'>,
): ExportPreset => ({
  builtIn: true,
  bitDepth: 8,
  quality: 92,
  size: { mode: 'original' },
  dpi: 72,
  colorSpace: 'srgb',
  sharpen: { target: 'none', amount: 'standard' },
  background: 'asEdited',
  watermark: false,
  metadata: KEEP_GPS.has(p.id) ? 'keepAll' : 'stripGps',
  rename: { enabled: false, template: '{orig}' },
  subfolder: p.id,
  ...p,
});

const CHECK_SPEC = 'check-app-spec';

export const BUILT_IN_EXPORT_PRESETS: ExportPreset[] = [
  base({
    id: 'full-res-jpeg',
    name: 'Full-res JPEG',
    nameEs: 'JPEG resolución completa',
    format: 'jpeg',
    quality: 92,
  }),
  base({
    id: 'web-2048',
    name: 'Web 2048 sRGB',
    nameEs: 'Web 2048 sRGB',
    format: 'jpeg',
    quality: 82,
    size: { mode: 'longEdge', w: 2048 },
    sharpen: { target: 'screen', amount: 'standard' },
  }),
  base({
    id: 'instagram-1080',
    name: 'Instagram 1080',
    nameEs: 'Instagram 1080',
    format: 'jpeg',
    quality: 85,
    size: { mode: 'width', w: 1080 },
    sharpen: { target: 'screen', amount: 'standard' },
  }),
  base({
    id: 'instagram-portrait',
    name: 'Instagram portrait 1080×1350',
    nameEs: 'Instagram vertical 1080×1350',
    format: 'jpeg',
    quality: 85,
    size: { mode: 'exact', w: 1080, h: 1350, fit: 'cover' },
  }),
  base({
    id: 'print-300',
    name: 'Print 300 dpi',
    nameEs: 'Impresión 300 ppp',
    format: 'jpeg',
    quality: 95,
    dpi: 300,
    sharpen: { target: 'glossy', amount: 'standard' },
  }),
  base({
    id: 'transparent-png',
    name: 'Transparent PNG',
    nameEs: 'PNG transparente',
    format: 'png',
    background: 'forceTransparent',
  }),
  base({
    id: 'transparent-png-2048',
    name: 'Transparent PNG 2048',
    nameEs: 'PNG transparente 2048',
    format: 'png',
    size: { mode: 'longEdge', w: 2048 },
    background: 'forceTransparent',
  }),
  base({
    id: 'png16-master',
    name: 'PNG 16-bit master',
    nameEs: 'PNG maestro 16 bits',
    format: 'png',
    bitDepth: 16,
    colorSpace: 'p3',
  }),
  base({
    id: 'tiff16',
    name: 'TIFF 16-bit',
    nameEs: 'TIFF 16 bits',
    format: 'tiff',
    bitDepth: 16,
    colorSpace: 'adobergb',
  }),
  base({
    id: 'menu-board-4k',
    name: 'Menu board 4K',
    nameEs: 'Menú en pantalla 4K',
    format: 'jpeg',
    quality: 90,
    size: { mode: 'longEdge', w: 3840 },
  }),
  base({
    id: 'printed-menu',
    name: 'Printed menu',
    nameEs: 'Menú impreso',
    format: 'jpeg',
    quality: 95,
    dpi: 300,
  }),
  base({
    id: 'delivery-listing',
    name: 'Delivery app listing',
    nameEs: 'Anuncio en app de entregas',
    format: 'jpeg',
    quality: 85,
    size: { mode: 'exact', w: 1200, h: 800, fit: 'cover' },
    background: 'forceColor',
    note: CHECK_SPEC,
  }),
  base({
    id: 'delivery-cutout',
    name: 'Delivery app cutout PNG',
    nameEs: 'Recorte PNG para app de entregas',
    format: 'png',
    size: { mode: 'exact', w: 1200, h: 1200, fit: 'contain' },
    background: 'forceTransparent',
    note: CHECK_SPEC,
  }),
  base({
    id: 'website-dish-card',
    name: 'Website dish card',
    nameEs: 'Tarjeta de platillo para sitio web',
    format: 'jpeg',
    quality: 82,
    size: { mode: 'exact', w: 800, h: 800, fit: 'cover' },
  }),
];

let db: UseStore | null = null;
const store = () => (db ??= createStore('epb-export-presets', 'presets'));

export async function listExportPresets(): Promise<ExportPreset[]> {
  let user: ExportPreset[] = [];
  try {
    user = (await get<ExportPreset[]>('list', store())) ?? [];
  } catch {
    /* built-ins only */
  }
  const byId = new Map(user.map((p) => [p.id, p]));
  const merged = BUILT_IN_EXPORT_PRESETS.map((b) => byId.get(b.id) ?? b);
  return [...merged, ...user.filter((p) => !BUILT_IN_EXPORT_PRESETS.some((b) => b.id === p.id))];
}

export async function saveExportPreset(p: ExportPreset) {
  let user: ExportPreset[] = [];
  try {
    user = (await get<ExportPreset[]>('list', store())) ?? [];
  } catch {
    /* start fresh */
  }
  await set('list', [...user.filter((x) => x.id !== p.id), p], store());
}

export function duplicateExportPreset(p: ExportPreset): ExportPreset {
  const id = `${p.id}-copy-${Date.now().toString(36)}`;
  return {
    ...structuredClone(p),
    id,
    builtIn: false,
    name: `${p.name} (copy)`,
    ...(p.nameEs ? { nameEs: `${p.nameEs} (copia)` } : {}),
    subfolder: id,
  };
}

export const presetLabel = (p: ExportPreset, lang: 'en' | 'es') =>
  lang === 'es' && p.nameEs ? p.nameEs : p.name;
export const needsSpecCheck = (p: ExportPreset) => p.note === CHECK_SPEC;
